import {
  AI_OBSERVATORY_LINK,
  AI_OBSERVATORY_PAPER_LINK,
  DATA_REPO_LINK,
  ECONOMIES_PAPER_LINK,
  OPEN_MODEL_DASHBOARD_LINK,
  OPEN_MODEL_DATASET_LINK,
} from "../constants";

export type PublicationAction = {
  label: string;
  href: string;
  external?: boolean;
};

export type PublicationMetadata = {
  year: string;
  program: string;
  logline: string;
  chips: string[];
  actions: PublicationAction[];
  featured?: boolean;
};

export const publicationMetadata: Record<string, PublicationMetadata> = {
  "The AI Observatory: A Public Measure of Real-World AI Use": {
    year: "2026",
    program: "AI Observatory",
    logline:
      "A public measurement platform for real-use AI conversations, model behavior, and user interaction patterns across sources, models, and time.",
    chips: ["23,158 conversations", "145 features", "Real-world use"],
    featured: true,
    actions: [
      { label: "Paper", href: AI_OBSERVATORY_PAPER_LINK },
      {
        label: "Observatory",
        href: AI_OBSERVATORY_LINK,
        external: true,
      },
    ],
  },
  "Economies of Open Intelligence: Tracing Power & Participation in the Model Ecosystem":
    {
      year: "2025",
      program: "Open Model Economy",
      logline:
        "A longitudinal measurement of power, participation, and transparency across the Hugging Face open-weight model ecosystem.",
      chips: ["851k models", "2.2B downloads", "Concentration"],
      featured: true,
      actions: [
        { label: "Paper", href: ECONOMIES_PAPER_LINK },
        {
          label: "Dashboard",
          href: OPEN_MODEL_DASHBOARD_LINK,
          external: true,
        },
        { label: "Dataset", href: OPEN_MODEL_DATASET_LINK, external: true },
      ],
    },
  "Bridging the Data Provenance Gap Across Text, Speech, and Video": {
    year: "2025",
    program: "Training Data Provenance",
    logline:
      "A multimodal audit tracing the sources, licenses, creators, languages, geographies, and derivation chains behind public training datasets.",
    chips: ["4,000 datasets", "608 languages", "Text, speech, video"],
    featured: true,
    actions: [
      { label: "Paper", href: "/Multimodal_Data_Provenance.pdf" },
      { label: "Explorer", href: "/data-provenance-explorer/dataset-explorer" },
      { label: "Collection", href: DATA_REPO_LINK, external: true },
    ],
  },
  "Consent in Crisis: The Rapid Decline of the AI Data Commons": {
    year: "2024",
    program: "Web Consent",
    logline:
      "A longitudinal audit of how web domains, terms of service, and robots.txt signals are restricting AI data collection.",
    chips: ["14,000 domains", "Consent signals", "NeurIPS 2024"],
    featured: true,
    actions: [
      { label: "Paper", href: "/Consent_in_Crisis.pdf" },
      {
        label: "Explorer",
        href: "/data-provenance-explorer/web-data-explorer",
      },
      { label: "Web page", href: "/consent-in-crisis-paper" },
    ],
  },
  "Data Authenticity, Consent, and Provenance for AI Are All Broken: What Will It Take to Fix Them?":
    {
      year: "2024",
      program: "Policy and Standards",
      logline:
        "A position paper on the missing infrastructure for responsible AI data authenticity, consent, and provenance.",
      chips: ["ICML Spotlight", "Standards", "Governance"],
      actions: [
        {
          label: "Proceedings",
          href: "https://proceedings.mlr.press/v235/longpre24b.html",
          external: true,
        },
      ],
    },
  "Robert Mahari and Shayne Longpre: “Discit ergo est: Training Data Provenance And Fair Use”":
    {
      year: "2023",
      program: "Law and Copyright",
      logline:
        "A legal analysis of training data provenance, fair use, and the copyright stakes of dataset construction.",
      chips: ["Fair use", "Copyright", "Law review"],
      actions: [
        {
          label: "Article",
          href: "https://www.networklawreview.org/mahari-longpre-generative-ai/",
          external: true,
        },
      ],
    },
  "Comment to U.S Copyright Office on Data Provenance and Copyright": {
    year: "2023",
    program: "Policy and Standards",
    logline:
      "A public comment on supervised datasets, licensing accuracy, and why provenance infrastructure matters for copyright policy.",
    chips: ["Copyright Office", "Fair use", "Licensing"],
    actions: [
      {
        label: "Comment",
        href: "https://hdl.handle.net/1721.1/154171",
        external: true,
      },
    ],
  },
  "The Data Provenance Initiative: A Large Scale Audit of Dataset Licensing & Attribution in AI":
    {
      year: "2024",
      program: "Training Data Provenance",
      logline:
        "The original large-scale DPI audit of dataset licensing, attribution, documentation, and provenance gaps in AI training data.",
      chips: ["Nature Machine Intelligence", "Licensing", "Attribution"],
      actions: [
        {
          label: "Paper",
          href: "https://doi.org/10.48550/arXiv.2310.16787",
          external: true,
        },
        { label: "Collection", href: DATA_REPO_LINK, external: true },
      ],
    },
};
